import CrudApiClient from './crudapiclient';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import { axiosInstance } from './axios';

type SearchFilter = {
  filterType: string;
  field: string;
  value: string;
};

type SearchQuery = {
  filters?: SearchFilter[];
  orderBy?: string;
  orderDirection?: string;
};

const MANIFEST_ITEM_TYPE_FILTER_FIELD = 'items.itemType';
const MANIFEST_ITEM_CODE_FILTER_FIELD = 'items.itemCode';

class ManifestApiClient extends CrudApiClient {
  constructor() {
    super(GATEWAY_API_URL + '/wms/manifest');
  }

  protected transformNode(node: any) {
    return node;
  }

  private async rewriteItemFiltersToManifestIds(query: SearchQuery) {
    const filters = query.filters ?? [];
    const itemFilters = filters.filter(
      filter =>
        filter.field === MANIFEST_ITEM_TYPE_FILTER_FIELD ||
        filter.field === MANIFEST_ITEM_CODE_FILTER_FIELD,
    );

    if (itemFilters.length === 0) {
      return query;
    }

    const manifestItemFilters = itemFilters.map(filter => ({
      ...filter,
      field: filter.field === MANIFEST_ITEM_TYPE_FILTER_FIELD ? 'itemType' : 'itemCode',
    }));

    const manifestItemResponse = await axiosInstance.post(
      `${GATEWAY_API_URL}/wms/manifest-item/search?page=1&size=500`,
      {
        filters: manifestItemFilters,
        orderBy: 'createdOn',
        orderDirection: 'DESC',
      },
    );

    const manifestIds = Array.from(
      new Set(
        (manifestItemResponse.data?.data ?? [])
          .map((item: any) => item?.manifestId)
          .filter((manifestId: number | null | undefined) => !!manifestId),
      ),
    );

    if (manifestIds.length === 0) {
      return {
        ...query,
        filters: [{ field: 'id', filterType: 'IN', value: '-1' }],
      };
    }

    const remainingFilters = filters.filter(
      filter =>
        filter.field !== MANIFEST_ITEM_TYPE_FILTER_FIELD &&
        filter.field !== MANIFEST_ITEM_CODE_FILTER_FIELD,
    );

    return {
      ...query,
      filters: [
        ...remainingFilters,
        {
          field: 'id',
          filterType: 'IN',
          value: manifestIds.join(','),
        },
      ],
    };
  }

  async search<T = any, Q = SearchQuery>(
    query: Q,
    page: number,
    size: number,
    meta: any,
  ): Promise<Array<T>> {
    const rewrittenQuery = await this.rewriteItemFiltersToManifestIds((query ?? {}) as SearchQuery);
    return super.search<T, SearchQuery>(rewrittenQuery, page, size, meta);
  }

  async complete(sorters: any, filters: any, payload: any, query: any, headers: any, meta: any) {
    if (meta?.manifestId) {
      const response = await axiosInstance.put(`${this.baseURL}/${meta.manifestId}/complete`);
      return response.data;
    }
    throw new Error('manifestId is required for complete action');
  }

  async discard(sorters: any, filters: any, payload: any, query: any, headers: any, meta: any) {
    if (meta?.manifestId) {
      const response = await axiosInstance.put(`${this.baseURL}/${meta.manifestId}/discard`);
      return response.data;
    }
    throw new Error('manifestId is required for discard action');
  }

  async 'add-item'(sorters: any, filters: any, payload: any, query: any, headers: any, meta: any) {
    if (meta?.manifestId && payload?.itemCode && payload?.itemType) {
      const response = await axiosInstance.post(`${this.baseURL}/${meta.manifestId}/item`, {
        itemCode: payload.itemCode,
        itemType: payload.itemType,
      });
      return response.data;
    }
    throw new Error('manifestId, itemCode, and itemType are required for add-item action');
  }

  async 'remove-item'(
    sorters: any,
    filters: any,
    payload: any,
    query: any,
    headers: any,
    meta: any,
  ) {
    if (meta?.manifestId && payload?.itemCode) {
      const response = await axiosInstance.delete(`${this.baseURL}/${meta.manifestId}/item`, {
        data: { itemCode: payload.itemCode },
      });
      return response.data;
    }
    throw new Error('manifestId and itemCode are required for remove-item action');
  }
}

export default ManifestApiClient;
